import { Pipe, PipeTransform } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { TranslateService } from '@services/translate/translate.service';
import * as _ from 'lodash';
import { ContentPipeService } from '@services/content-pipe/content-pipe.service';

@Pipe({
    name: 'contentEvent',
    standalone: false
})
export class EventPipe implements PipeTransform {

  today = null;
  future = null;
  dateFormat: any = { day: '2-digit', month: '2-digit' };
  customIds = [];

  private static getDateUnix(dateString, isEndDate = false) {
    const res = EventPipe.getDate(dateString, isEndDate);
    return !!res ? res.getTime() / 1000 : null;
  }

  private static getDate(dateString, isEndDate = false) {
    if (!dateString) return null;
    const res = /\d?\d\.\d?\d\.(\d\d|\d\d\d\d)/.test(dateString) ? new Date(dateString.split('.').reverse().join('/')) : new Date(dateString);
    if (isEndDate) {
      res.setHours(23, 59, 59);
    }
    return res;
  }

  private static getNulledDate() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  private static format(date, format = 'DD.MM.YYYY') {
    const groups = format.split('.');
    return groups.map(group => {
      const value = group.startsWith('D') ? date.getDate() :
        group.startsWith('M') ? date.getMonth() + 1 :
          date.getFullYear();

      if (group.startsWith('D') || group.startsWith('M')) {
        return String(value).padStart(group.length, '0');
      } else {
        return group.length === 4 ? value : String(value).slice(-2);
      }
    }).join('.');
  }

  constructor(private contentPipeService: ContentPipeService, private translate: TranslateService) {
    const day = EventPipe.getNulledDate();
    this.today = +day.getTime() / 1000;
    this.future = +day.setMonth(day.getMonth() + 6) / 1000;
    /* future: we do not display events more than 6 months in the future */
  }


  transform(entry: any): Models.EventEntry | Models.EventEntry[] {
    if (!entry) {
      return null;
    }

    /* event detail */
    if (!entry.content) {
      return null;
    }

    const event: any = new Models.EventEntry();

    let media = this.contentPipeService.getMedia(entry);
    const documents = this.contentPipeService.getDocuments(entry);

    media = _.orderBy(media, ['type'], ['asc']);

    const eventContent = {
      sourceId: entry._id,
      title: entry.title,
      subtitle: entry.content.subtitle,
      teaser: entry.content.introduction,
      description: entry.content.textContent,
      additionalInformation: entry.content.openingHours,
      website: entry.content.website,
      dateStrings: this.removePastDates(entry.content.dateStrings),
      dateRanges: this.removePastItems(entry.content.dateRanges),
      shortDateStrings: null,
      media,
      documents,
      orderTimestamp: entry.content.orderTimestamp
    };

    if ((!eventContent.dateRanges || !eventContent.dateRanges.length) && entry.content &&
      ((entry.content.multipleDates && entry.content.multipleDates.length) || (entry.content.dates && entry.content.dates.length))) {
      const ranges = [];
      for (const date of entry.content.multipleDates || (entry.content.dates.length ? entry.content.dates : [entry.content.dates])) {
        const start = new Date(date.startDate);
        start.setHours(0, 0);
        const end = date.endDate ? new Date(date.endDate) : new Date(start.getTime());
        end.setHours(23, 59);
        ranges.push({ start: start.getTime() / 1000, end: end.getTime() / 1000 });
      }
      eventContent.dateRanges = ranges;
    }

    this.enhanceEventDetail(eventContent, entry);

    if (entry.content.dates) {
      eventContent.shortDateStrings = this.prettifyDate(entry);
    }
    if (eventContent && eventContent.dateRanges && entry && entry.content && entry.content.dates
      && eventContent.dateRanges.length === entry.content.dates.length) {
      for (let i = 0; i < eventContent.dateRanges.length; i++) {
        eventContent.dateRanges[i]['days'] = this.getDaysArray(entry.content.dates[i]);
      }
    }

    event.populateFromJSON(eventContent);
    event.contentType = 'event';
    return event;

  }

  private enhanceEventDetail(eventContent, entry) {
    const data = entry.content;
    if (data.location) {
      if (data.location.coordinates) {
        eventContent.latitude = data.location.coordinates.latitude;
        eventContent.longitude = data.location.coordinates.longitude;
      }
      eventContent.street = data.location.address.trim();
      eventContent.city = data.location.city.trim();
      eventContent.zip = data.location.zip.trim();
    }

    if (data.contact && data.contact[0]) {
      for (const variant of data.contact) {
        const contactVariant = variant;
        if (contactVariant.type === 'phone') {
          eventContent.phone = contactVariant.value;
        }
        if (contactVariant.type === 'fax') {
          eventContent.fax = contactVariant.value;
        }
        if (contactVariant.type === 'email') {
          eventContent.email = contactVariant.value;
        }
      }
    }

    let raw;
    if (entry && entry.content && entry.content.raw) {
      try {
        raw = JSON.parse(entry.content.raw);
      } catch (err) {
        console.warn('could not parse raw for entry', entry.title, err);
      }
    }

    if (data.bookable) {
      eventContent.bookable = data.bookable;
      eventContent.bookingUrl = data.bookingUrl;
      eventContent.bookingText = data.bookingText;
      eventContent.products = data.products;
      eventContent.productsType = data.productsType;
    } else if (raw && raw.bookable && raw.bookingUrl) {
      eventContent.bookable = raw.bookable;
      eventContent.bookingUrl = raw.bookingUrl;
      eventContent.bookingText = raw.bookingText;
    }

    if (entry.content.dates
      && ((_.isArray(data.dates) && entry.content.dates[0] && entry.content.dates[0].startDate && entry.content.dates[0].daily !== undefined)
        || (!_.isArray(data.dates) && data.dates.startDate && data.dates.daily !== undefined))) {
      eventContent.dateStrings = this.getIbexfusionDate(entry).ibexfusionDates;
      eventContent.shortDateStrings = this.getIbexfusionDate(entry, true).ibexfusionDates;
    }
    if (!eventContent.dateRanges && entry.content.validity && entry.content.validity.start && entry.content.validity.end) {
      eventContent.dateRanges = [entry.content.validity]
    }
  }

  private prettifyDate(event: any) {

    const prettyDateString = [];
    if (!_.isArray(event.content.dates)) {
      event.content.dates = [event.content.dates];
    }

    const eventDates = event.content.multipleDates ? event.content.multipleDates : event.content.dates;

    for (const date of eventDates) {
      if (!date.startDate && !date.endDate) {
        continue;
      }

      /*
       * ibexfusion dates are in the javascript Date format
       * Guidle dates are german formatted strings
       * We check for the T in the string - to find the javasript Dates
       */

      const startDate = date.startDate.indexOf('T') > -1 ? new Date(date.startDate).toLocaleDateString('de-CH', this.dateFormat) : date.startDate;

      let endDate;
      if (date.endDate) {
        endDate = date.endDate.indexOf('T') > -1 ? new Date(date.endDate).toLocaleDateString('de-CH', this.dateFormat) : date.endDate;
      }
      let dateString = startDate.substring(0, 5) + (endDate && endDate !== startDate ? ' - ' + endDate.substring(0, 5) : '');
      if (date.days) {
        dateString += ' ' + this.getDayString(date.days);
      }
      let timeString = '';
      if (date.startTime) {
        timeString += date.startTime + (date.endTime ? ' - ' + date.endTime : '');
      }
      if (timeString.length) {
        dateString += ', ' + timeString;
      }
      prettyDateString.push(dateString);
    }
    return prettyDateString;

  }

  private getDayString(days) {
    const weekdayArray = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    const dayArray = weekdayArray.map(day => days.includes(day));

    if (dayArray.every(day => day) || !dayArray.some(Boolean)) return null;

    const rangePattern = (start, end) => `|${start}| - |${end}|`;

    let dayString = '';
    let i = 0;

    while (i < dayArray.length) {
      if (dayArray[i]) {
        let j = i;
        while (j < dayArray.length && dayArray[j]) j++;  // Find consecutive true values
        const range = j - i > 1 ? rangePattern(weekdayArray[i], weekdayArray[j - 1]) : `|${weekdayArray[i]}|`;
        dayString += (dayString ? ',' : '') + range;
        i = j;
      } else {
        i++;
      }
    }

    // tslint:disable-next-line:no-shadowed-variable
    return dayString.replace(/\|(\d?\d)\|/g, (_, day) => this.getTranslatedWeekday(day, true)) || null;
  }

  private getIbexfusionDate(entry: any, short = false): { ibexfusionDate: string, ibexfusionDates: string[] } {
    const multipleDates = entry.content.multipleDates || (entry.content.dates ? (_.isArray(entry.content.dates) ? entry.content.dates : [entry.content.dates]) : null);

    if (!multipleDates) return null;

    const now = new Date().getTime() / 1000;
    let closestIndex = 0;
    let lowestDifference = Infinity;
    const multipleIbexfusionDates = multipleDates.map((date, i) => {
      const startDateUnix = EventPipe.getDateUnix(date.startDate);
      const endDateUnix = EventPipe.getDateUnix(date.endDate, true);

      if (endDateUnix && endDateUnix < now && multipleDates.length > 1) {
        return null;
      }

      if (startDateUnix && startDateUnix - now < lowestDifference) {
        lowestDifference = startDateUnix - now;
        closestIndex = i;
      }

      return this.getIbexfusionDateString(date, short);
    }).filter(Boolean);

    return { ibexfusionDates: multipleIbexfusionDates, ibexfusionDate: multipleIbexfusionDates[closestIndex] };
  }

  private getIbexfusionDateString(date, short = false) {
    const { startDate, endDate, startTime, endTime, daily, days } = date;
    const from = this.translate?.get('general.from') || '';
    const to = this.translate?.get('general.to') || '';
    const start = EventPipe.getDate(startDate);
    const end = EventPipe.getDate(endDate, true);

    if (!start && !end) return null;

    const dateFormat = ((end && start.getFullYear() === end.getFullYear()) || !end && start.getFullYear() === new Date().getFullYear()) ? 'DD.MM.' : 'DD.MM.YYYY';
    const dayString = days ? this.getDayString(days) : null;

    if (daily) {
      let res = EventPipe.format(start, dateFormat) + (end && EventPipe.format(start, 'D.M.YYYY') !== EventPipe.format(end, 'D.M.YYYY') ? ' - ' + EventPipe.format(end, dateFormat) : '');
      if (dayString) res += ' / ' + dayString;
      if (!short && startTime) {
        res += ` ${startTime}${endTime ? ' - ' + endTime : ''}`;
      }
      return res;
    }

    // Handle non-daily events
    const timeString = startTime ? (endTime ? `${startTime} - ${endTime}` : `${from} ${startTime}`) : '';
    const dateRangeString = end ? `${EventPipe.format(start, dateFormat)} ${timeString} ${to} ${EventPipe.format(end, dateFormat)} ${endTime || ''}` : startTime ? `${EventPipe.format(start, dateFormat)} ${from} ${startTime}` : `${EventPipe.format(start, dateFormat)}`;

    return dateRangeString.trim();
  }

  private getTranslatedWeekday(num, short = false): any {
    return this.translate.get(`general.weekday.${short ? 'short.' : ''}${num % 7}`);
  }

  private getDaysArray(range: any): number[] {
    const shortDayTranslations = [['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'], ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'], ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'], ['Do', 'Lu', 'Ma', 'Me', 'Gi', 'Ve', 'Sa']];
    return range?.days?.split(',').reduce((res, day) => {
      const arr = shortDayTranslations.find(arra => arra.includes(day));
      if (arr) res.push(arr.indexOf(day));
      return res;
    }, []);
  }

  private removePastItems(array: { start: number, end: number }[]): { start: number, end: number }[] {
    const currentTime = Math.floor(Date.now() / 1000);
    return array?.filter(item => item?.end > currentTime);
  }

  private removePastDates(dates: string[]): string[] {
    const currentDate = new Date();

    return dates?.filter(dateString => {
      const [day, month, year] = dateString.split('.').map(Number);
      const date = new Date(year, month - 1, day); // Convert string to Date object
      return date >= currentDate; // Keep only future or present dates
    });
  }
}
